import { useCallback, useEffect, useState } from "react";
import {
  COLUMN_TYPES,
  describeScope,
  endpointExample,
  endpointUrl,
  eventsUrl,
  formatDbBytes,
  formatRelativeTime,
  isValidResourceName,
} from "@saucer/core";
import type {
  ApiToken,
  ApiTokenSecret,
  Column,
  ColumnType,
  DataSource,
  Pipe,
  PipeNode,
  ScopeType,
} from "@saucer/core";
import {
  createApiToken,
  createDataSource,
  deleteApiToken,
  deleteDataSource,
  deletePipe,
  explainPipe,
  listDataSources,
  listPipes,
  listWorkspaceStats,
  listWorkspaceTokens,
  savePipe,
} from "../api.js";
import type { EndpointWindow } from "../api.js";
import { SECTIONS } from "../components/managed-docs-content.js";
import { navigate } from "../router.js";

/**
 * One workspace: its data sources, its pipes, the endpoints they publish and
 * the tokens that reach them.
 *
 * The thing this page has to make obvious is the difference between a pipe that
 * is saved and a pipe that is *published*, because only the second one is a live
 * API. Publishing is what pushes the compiled SQL to the machine that serves it.
 */
export function Workspace({ workspaceId }: { workspaceId: string }) {
  const [tab, setTab] = useState<
    "datasources" | "pipes" | "tokens" | "observability" | "docs"
  >("datasources");
  const [error, setError] = useState<string | null>(null);
  const [apiHost, setApiHost] = useState("api.saucer.sh");

  return (
    <main className="stack">
      <section className="stack stack--tight">
        <div className="section-head">
          <h1>Workspace</h1>
          <button className="button button--quiet" type="button" onClick={() => navigate("/databases")}>
            All workspaces
          </button>
        </div>
        <p className="meta">
          Data sources hold the events, pipes shape them, and a published pipe answers on{" "}
          <code className="mono">{apiHost}</code>.
        </p>
      </section>

      {error ? (
        <div className="notice notice--error">
          <p>{error}</p>
        </div>
      ) : null}

      <nav className="seg" role="tablist" aria-label="Workspace sections">
        {(["datasources", "pipes", "tokens", "observability", "docs"] as const).map((name) => (
          <button
            key={name}
            role="tab"
            aria-selected={tab === name}
            className={`seg__option${tab === name ? " seg__option--on" : ""}`}
            type="button"
            onClick={() => setTab(name)}
          >
            {name === "datasources" ? "data sources" : name}
          </button>
        ))}
      </nav>

      {tab === "datasources" ? (
        <DataSources workspaceId={workspaceId} onError={setError} onHost={setApiHost} />
      ) : null}
      {tab === "pipes" ? <Pipes workspaceId={workspaceId} onError={setError} /> : null}
      {tab === "tokens" ? <Tokens workspaceId={workspaceId} onError={setError} /> : null}
      {tab === "observability" ? <Observability workspaceId={workspaceId} onError={setError} /> : null}
      {tab === "docs" ? <WorkspaceDocs /> : null}
    </main>
  );
}

/* -- docs ------------------------------------------------------------------ */

/**
 * The same documentation the public page renders, in the place where the
 * questions actually occur.
 *
 * Someone stuck on what `{{Int64(limit, 10)}}` does is two tabs away from the
 * pipe editor, not one navigation away from the app. Sections are collapsed by
 * default because this is a reference to dip into, not a page to read — and an
 * accordion keeps the tab from opening onto three thousand words.
 */
function WorkspaceDocs() {
  const [open, setOpen] = useState<string | null>("quickstart");

  return (
    <section className="stack stack--tight">
      <p className="meta">
        The full reference, also at{" "}
        <a href="/managed/docs" onClick={() => navigate("/managed/docs")}>
          /managed/docs
        </a>
        .
      </p>

      <div className="stack stack--tight">
        {SECTIONS.map((section) => {
          const isOpen = open === section.id;
          return (
            <div key={section.id} className="ws__doc">
              <button
                type="button"
                className="ws__doc-head"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : section.id)}
              >
                <span>{section.title}</span>
                <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen ? <div className="ws__doc-body">{section.body}</div> : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* -- data sources ---------------------------------------------------------- */

function DataSources({
  workspaceId,
  onError,
  onHost,
}: {
  workspaceId: string;
  onError: (message: string | null) => void;
  onHost: (host: string) => void;
}) {
  const [sources, setSources] = useState<DataSource[] | null>(null);
  const [host, setHost] = useState("api.saucer.sh");
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const body = await listDataSources(workspaceId);
      setSources(body.datasources);
      setHost(body.ingestHost);
      onHost(body.ingestHost);
      onError(null);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "Could not load data sources.");
    }
  }, [workspaceId, onError, onHost]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>Data sources</h2>
        <button className="button button--quiet" type="button" onClick={() => setCreating((open) => !open)}>
          {creating ? "Cancel" : "New data source"}
        </button>
      </div>

      {creating ? (
        <NewDataSource
          workspaceId={workspaceId}
          onError={onError}
          onCreated={() => {
            setCreating(false);
            void refresh();
          }}
        />
      ) : null}

      {sources === null ? (
        <p className="meta">Loading…</p>
      ) : sources.length === 0 ? (
        <p className="meta">
          Nothing yet. A data source is a table plus the two decisions that make queries over
          it fast: the sorting key and the partition.
        </p>
      ) : (
        <ul className="rows">
          {sources.map((source) => (
            <li key={source.id} className="rows__row db__row">
              <div className="db__ident">
                <span className="mono">{source.name}</span>
                <span className="meta">
                  {source.columns.length} columns · sorted by{" "}
                  <span className="mono">{source.sortingKey.join(", ") || "nothing"}</span>
                  {source.rows > 0
                    ? ` · ${source.rows.toLocaleString()} rows, ${formatDbBytes(source.bytes)}`
                    : " · empty"}
                </span>
                <code className="row__hint mono db__where">{eventsUrl(host, source.name)}</code>
              </div>
              <span className="db__actions">
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Drop ${source.name} and everything in it?`)) return;
                    try {
                      await deleteDataSource(workspaceId, source.name);
                      await refresh();
                    } catch (cause) {
                      onError(cause instanceof Error ? cause.message : "Could not drop that.");
                    }
                  }}
                >
                  Drop
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const BLANK_COLUMN: Column = { name: "", type: "String" };

function NewDataSource({
  workspaceId,
  onError,
  onCreated,
}: {
  workspaceId: string;
  onError: (message: string | null) => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [columns, setColumns] = useState<Column[]>([
    { name: "timestamp", type: "DateTime" },
    { name: "url", type: "String" },
  ]);
  const [sortingKey, setSortingKey] = useState("timestamp");
  const [partitionBy, setPartitionBy] = useState("toYYYYMM(timestamp)");
  const [busy, setBusy] = useState(false);

  const named = name.trim();
  const valid = isValidResourceName(named) && columns.every((column) => column.name.trim());

  return (
    <form
      className="ws__form stack stack--tight"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!valid || busy) return;
        setBusy(true);
        try {
          await createDataSource(workspaceId, {
            name: named,
            columns,
            sortingKey: sortingKey.split(",").map((key) => key.trim()).filter(Boolean),
            partitionBy,
            ttl: "",
          });
          onCreated();
        } catch (cause) {
          onError(cause instanceof Error ? cause.message : "Could not create that.");
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="field">
        <span className="meta">Name</span>
        <input
          className="input mono"
          value={name}
          placeholder="hits"
          onChange={(event) => setName(event.target.value.toLowerCase())}
        />
      </div>

      <div className="field">
        <span className="meta">Columns</span>
        {columns.map((column, index) => (
          <div key={index} className="row">
            <input
              className="input mono"
              value={column.name}
              placeholder="column_name"
              onChange={(event) => {
                const next = [...columns];
                next[index] = { ...column, name: event.target.value };
                setColumns(next);
              }}
            />
            <select
              className="input mono"
              value={column.type}
              onChange={(event) => {
                const next = [...columns];
                next[index] = { ...column, type: event.target.value as ColumnType };
                setColumns(next);
              }}
            >
              {COLUMN_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <button
              className="button button--quiet"
              type="button"
              disabled={columns.length === 1}
              onClick={() => setColumns(columns.filter((_, at) => at !== index))}
            >
              Remove
            </button>
          </div>
        ))}
        <span>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => setColumns([...columns, { ...BLANK_COLUMN }])}
          >
            Add column
          </button>
        </span>
      </div>

      <div className="field">
        <span className="meta">
          Sorting key — the physical order of the data, and the reason a filter is fast. Put the
          columns you filter on most first. It cannot be changed later.
        </span>
        <input
          className="input mono"
          value={sortingKey}
          onChange={(event) => setSortingKey(event.target.value)}
        />
      </div>

      <div className="field">
        <span className="meta">Partition — usually a month, so dropping old data is a file delete.</span>
        <input
          className="input mono"
          value={partitionBy}
          onChange={(event) => setPartitionBy(event.target.value)}
        />
      </div>

      <span>
        <button className="button" type="submit" disabled={!valid || busy}>
          {busy ? "Creating…" : "Create data source"}
        </button>
      </span>
    </form>
  );
}

/* -- pipes ----------------------------------------------------------------- */

function Pipes({
  workspaceId,
  onError,
}: {
  workspaceId: string;
  onError: (message: string | null) => void;
}) {
  const [pipes, setPipes] = useState<Pipe[] | null>(null);
  const [apiHost, setApiHost] = useState("api.saucer.sh");
  const [editing, setEditing] = useState<Pipe | "new" | null>(null);

  const refresh = useCallback(async () => {
    try {
      const body = await listPipes(workspaceId);
      setPipes(body.pipes);
      setApiHost(body.apiHost);
      onError(null);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "Could not load pipes.");
    }
  }, [workspaceId, onError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (editing) {
    return (
      <PipeEditor
        workspaceId={workspaceId}
        apiHost={apiHost}
        pipe={editing === "new" ? null : editing}
        onError={onError}
        onDone={() => {
          setEditing(null);
          void refresh();
        }}
      />
    );
  }

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>Pipes</h2>
        <button className="button button--quiet" type="button" onClick={() => setEditing("new")}>
          New pipe
        </button>
      </div>

      {pipes === null ? (
        <p className="meta">Loading…</p>
      ) : pipes.length === 0 ? (
        <p className="meta">
          A pipe is a chain of SQL nodes. Publish one of them and it becomes an HTTP endpoint.
        </p>
      ) : (
        <ul className="rows">
          {pipes.map((pipe) => (
            <li key={pipe.id} className="rows__row db__row">
              <div className="db__ident">
                <span className="mono">
                  {pipe.name}
                  {pipe.endpointNode ? (
                    <span className="ws__badge">live</span>
                  ) : (
                    <span className="ws__badge ws__badge--draft">draft</span>
                  )}
                </span>
                <span className="meta">
                  {pipe.nodes.length} {pipe.nodes.length === 1 ? "node" : "nodes"}
                  {pipe.params.length > 0
                    ? ` · ${pipe.params.map((param) => `${param.name}:${param.type}`).join(", ")}`
                    : null}
                </span>
                {pipe.endpointNode ? (
                  <code className="row__hint mono db__where">
                    {endpointUrl(apiHost, pipe.name)}
                  </code>
                ) : null}
              </div>
              <span className="db__actions">
                <button className="button button--quiet" type="button" onClick={() => setEditing(pipe)}>
                  Edit
                </button>
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Delete pipe ${pipe.name}?`)) return;
                    try {
                      await deletePipe(workspaceId, pipe.name);
                      await refresh();
                    } catch (cause) {
                      onError(cause instanceof Error ? cause.message : "Could not delete that.");
                    }
                  }}
                >
                  Delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const STARTER_SQL = `SELECT
  toStartOfHour(timestamp) AS hour,
  count() AS hits
FROM hits
WHERE timestamp >= now() - toIntervalHour({{Int64(hours, 24)}})
GROUP BY hour
ORDER BY hour DESC`;

function PipeEditor({
  workspaceId,
  apiHost,
  pipe,
  onError,
  onDone,
}: {
  workspaceId: string;
  apiHost: string;
  pipe: Pipe | null;
  onError: (message: string | null) => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(pipe?.name ?? "");
  const [nodes, setNodes] = useState<PipeNode[]>(
    pipe?.nodes ?? [{ name: "endpoint", sql: STARTER_SQL }],
  );
  const [published, setPublished] = useState<string | null>(pipe?.endpointNode ?? null);
  const [cacheSeconds, setCacheSeconds] = useState(pipe?.cacheSeconds ?? 0);
  const [maxThreads, setMaxThreads] = useState(pipe?.maxThreads ?? 2);
  const [compiled, setCompiled] = useState<{ sql: string; params: { name: string; type: string }[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const named = name.trim();
  const valid = isValidResourceName(named) && nodes.every((node) => node.name && node.sql.trim());

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>{pipe ? `Edit ${pipe.name}` : "New pipe"}</h2>
        <button className="button button--quiet" type="button" onClick={onDone}>
          Back
        </button>
      </div>

      <div className="field">
        <span className="meta">Name — this becomes the endpoint's path.</span>
        <input
          className="input mono"
          value={name}
          placeholder="session_stats"
          disabled={Boolean(pipe)}
          onChange={(event) => setName(event.target.value.toLowerCase())}
        />
      </div>

      {nodes.map((node, index) => (
        <div key={index} className="field">
          <div className="row">
            <input
              className="input mono"
              value={node.name}
              onChange={(event) => {
                const renamed = event.target.value.toLowerCase();
                const next = [...nodes];
                next[index] = { ...node, name: renamed };
                setNodes(next);
                // `published` holds a node *name*, so a rename has to carry it
                // along or the form keeps pointing at a node that is gone and
                // sends it to the server on save.
                if (published === node.name) setPublished(renamed);
              }}
            />
            <label className="meta ws__publish">
              <input
                type="radio"
                name="published"
                checked={published === node.name}
                onChange={() => setPublished(node.name)}
              />
              publish this node
            </label>
            {nodes.length > 1 ? (
              <button
                className="button button--quiet"
                type="button"
                onClick={() => {
                  setNodes(nodes.filter((_, at) => at !== index));
                  // Deleting the published node unpublishes the pipe rather
                  // than leaving a dangling endpoint name behind.
                  if (published === node.name) setPublished(null);
                }}
              >
                Remove
              </button>
            ) : null}
          </div>
          <textarea
            className="input mono ws__sql"
            rows={10}
            value={node.sql}
            spellCheck={false}
            onChange={(event) => {
              const next = [...nodes];
              next[index] = { ...node, sql: event.target.value };
              setNodes(next);
            }}
          />
        </div>
      ))}

      <div className="ws__tuning">
        <label className="field">
          <span className="meta">
            Cache for (seconds) — 0 is off. A cached answer is served from memory in
            microseconds instead of milliseconds, and may be up to this old.
          </span>
          <input
            className="input mono"
            type="number"
            min={0}
            max={3600}
            value={cacheSeconds}
            onChange={(event) => setCacheSeconds(Math.max(0, Number(event.target.value) || 0))}
          />
        </label>
        <label className="field">
          <span className="meta">
            Threads — 2 suits a serving query. Raise it only if this endpoint really
            aggregates millions of rows.
          </span>
          <input
            className="input mono"
            type="number"
            min={0}
            max={32}
            value={maxThreads}
            onChange={(event) => setMaxThreads(Math.max(0, Number(event.target.value) || 0))}
          />
        </label>
      </div>

      <div className="row">
        <button
          className="button button--quiet"
          type="button"
          onClick={() => setNodes([...nodes, { name: `node_${nodes.length + 1}`, sql: "SELECT 1" }])}
        >
          Add node
        </button>
        <button
          className="button button--quiet"
          type="button"
          disabled={!published}
          title="Show the single statement this compiles to, and the parameters it takes"
          onClick={async () => {
            try {
              setCompiled(await explainPipe(workspaceId, named || "draft", nodes, published!));
            } catch (cause) {
              onError(cause instanceof Error ? cause.message : "Could not compile that.");
            }
          }}
        >
          Show compiled SQL
        </button>
        <button
          className="button"
          type="button"
          disabled={!valid || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await savePipe(workspaceId, named, {
                description: "",
                nodes,
                endpointNode: published,
                cacheSeconds,
                maxThreads,
              });
              onDone();
            } catch (cause) {
              onError(cause instanceof Error ? cause.message : "Could not save that.");
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : published ? "Save and publish" : "Save as draft"}
        </button>
      </div>

      {compiled ? (
        <div className="stack stack--tight">
          <span className="meta">
            This is the statement that will run. Nodes become CTEs, so the chain costs nothing
            at query time.
          </span>
          <pre className="ch__code mono">
            <code>{compiled.sql}</code>
          </pre>
          {compiled.params.length > 0 ? (
            <>
              <span className="meta">Parameters, derived from the SQL:</span>
              <pre className="ch__code mono">
                <code>{endpointExample(apiHost, named || "pipe", compiled.params as never)}</code>
              </pre>
            </>
          ) : (
            <span className="meta">No parameters.</span>
          )}
        </div>
      ) : null}
    </section>
  );
}

/* -- tokens ---------------------------------------------------------------- */

function Tokens({
  workspaceId,
  onError,
}: {
  workspaceId: string;
  onError: (message: string | null) => void;
}) {
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [fresh, setFresh] = useState<ApiTokenSecret | null>(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ScopeType>("read");
  const [resource, setResource] = useState("");

  const refresh = useCallback(async () => {
    try {
      setTokens(await listWorkspaceTokens(workspaceId));
      onError(null);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "Could not load tokens.");
    }
  }, [workspaceId, onError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>API tokens</h2>
        <span className="meta">
          These reach the endpoints, not the dashboard. Scope them to one resource so the token
          in a web page cannot read anything else.
        </span>
      </div>

      {fresh ? (
        <div className="notice stack stack--tight">
          <span className="meta">
            Shown once. It is stored only as a hash, here and on the box, so there is no way to
            show it again.
          </span>
          <code className="token mono">{fresh.token}</code>
          <span>
            <button className="button" type="button" onClick={() => setFresh(null)}>
              I have copied it
            </button>
          </span>
        </div>
      ) : null}

      <form
        className="row"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!name.trim()) return;
          try {
            const created = await createApiToken(workspaceId, name.trim(), [
              { type: scope, resource: scope === "admin" ? null : resource.trim() || null },
            ]);
            setFresh(created);
            setName("");
            setResource("");
            await refresh();
          } catch (cause) {
            onError(cause instanceof Error ? cause.message : "Could not create that token.");
          }
        }}
      >
        <input
          className="input"
          value={name}
          placeholder="token name"
          onChange={(event) => setName(event.target.value)}
        />
        <select
          className="input mono"
          value={scope}
          onChange={(event) => setScope(event.target.value as ScopeType)}
        >
          <option value="read">read</option>
          <option value="append">append</option>
          <option value="admin">admin</option>
        </select>
        {scope !== "admin" ? (
          <input
            className="input mono"
            value={resource}
            placeholder={scope === "read" ? "pipe name (blank = all)" : "data source (blank = all)"}
            onChange={(event) => setResource(event.target.value)}
          />
        ) : null}
        <button className="button" type="submit">
          Create
        </button>
      </form>

      {tokens === null ? (
        <p className="meta">Loading…</p>
      ) : tokens.length === 0 ? (
        <p className="meta">No tokens yet.</p>
      ) : (
        <ul className="rows">
          {tokens.map((token) => (
            <li key={token.id} className="rows__row db__row">
              <div className="db__ident">
                <span className="mono">{token.name}</span>
                <span className="meta">
                  {token.scopes.map(describeScope).join(", ")} · created{" "}
                  {formatRelativeTime(token.createdAt)}
                </span>
              </div>
              <span className="db__actions">
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Revoke ${token.name}? It stops working immediately.`)) return;
                    try {
                      await deleteApiToken(workspaceId, token.id);
                      await refresh();
                    } catch (cause) {
                      onError(cause instanceof Error ? cause.message : "Could not revoke that.");
                    }
                  }}
                >
                  Revoke
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* -- observability --------------------------------------------------------- */

function Observability({
  workspaceId,
  onError,
}: {
  workspaceId: string;
  onError: (message: string | null) => void;
}) {
  const [windows, setWindows] = useState<EndpointWindow[] | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setWindows(await listWorkspaceStats(workspaceId));
        onError(null);
      } catch (cause) {
        onError(cause instanceof Error ? cause.message : "Could not load stats.");
      }
    })();
  }, [workspaceId, onError]);

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>Endpoint latency</h2>
        <span className="meta">
          Measured beside the database, where the requests are actually answered.
        </span>
      </div>

      {windows === null ? (
        <p className="meta">Loading…</p>
      ) : windows.length === 0 ? (
        <p className="meta">
          Nothing recorded yet. Numbers appear after a published endpoint has served traffic and
          the box has reported its next window.
        </p>
      ) : (
        <div className="ch__table-scroll">
          <table className="ch__table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th className="ch__num">Requests</th>
                <th className="ch__num">Errors</th>
                <th className="ch__num">p50</th>
                <th className="ch__num">p95</th>
                <th className="ch__num">p99</th>
                <th className="ch__num">Rows read</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {windows.map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.pipe}</td>
                  <td className="ch__num mono">{row.requests.toLocaleString()}</td>
                  <td className="ch__num mono">{row.errors}</td>
                  <td className="ch__num mono">{row.p50}ms</td>
                  <td className="ch__num mono">{row.p95}ms</td>
                  <td className="ch__num mono">{row.p99}ms</td>
                  <td className="ch__num mono">{row.rowsRead.toLocaleString()}</td>
                  <td className="meta">{formatRelativeTime(row.recordedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
